import axios from "@/shared/scripts/AutoRefreshAxios";
import { Method } from "axios";
import store from "../store";
import {ApiError} from "@/shared/interfaces/ApiError";
import {ApiErrorWrapper} from "@/shared/interfaces/ApiErrorWrapper";
import {removeEmptyObjects} from "@/shared/scripts/helpers/object";
import {Classified} from "@/shared/interfaces/classified/Classified.interface";
import { forEach } from "lodash";
import {ACCEPTABLE_PROPERTY_TYPES} from "@/shared/scripts/helpers/constants/propertyTypes";

interface ClassifiedRequestConfig {
    method: Method;
    uri: string;
    callbackFunction: (success: boolean) => void;
}

export function resetBasicInfo(): void {
    store.commit("FormModule/deleteValueTransaction", "publication");
    store.commit("FormModule/deleteValueTransaction", "transaction");
    store.commit("FormModule/deleteValueTransaction", "optimizedEncoding");
    store.commit("FormModule/deleteValueOrder", "order");

    // Trigger BasicInfo component to reload basic info information
    store.commit("FormModule/addValueUI", { "path": "shouldReloadBasicInfo", "value": true });
}

export function resetPricingPack(): void {
    store.commit("FormModule/deleteValueTransaction", "publication");
    store.commit("FormModule/deleteValueTransaction", "transaction");
    store.commit("FormModule/deleteValueTransaction", "optimizedEncoding");
    store.commit("FormModule/deleteValueOrder", "order");

    // Trigger PackSelection component to reload packs
    store.commit("FormModule/addValueUI", { "path": "shouldReloadPacks", "value": true });
}

export function resetPayment(): void {
    store.commit("OrdersModule/transactions/paymentMethod", null);
    store.commit("AgreementModule/termsAndConditions", false);

    // Trigger Payment component to reload payment information
    store.commit("FormModule/addValueUI", { "path": "shouldReload", "value": true });
}

// strip classified data to the bare minimum
export function minimizeClassified(classified : Classified) : Classified {
    const authorizedFields: Array<string> = [
        "property",
        "transaction",
    ];

    for (const property in classified) {
        if (authorizedFields.indexOf(property) === -1) {
            delete classified[property];
        }
    }

    return classified;
}

// kill certain criteria on property type change
export function killNotRelevantCriteria(
    propertyType: string,
    propertySubtype: string|null,
    transactionType: string,
    transactionSubtype: string|null,
) : void {

    const criteriaToKill: Array<string> = [];

    if (["APARTMENT", "OFFICE", "INDUSTRY", "COMMERCIAL", "GARAGE"].indexOf(propertyType) == -1) {
        criteriaToKill.push.apply(criteriaToKill, [
            "property.location.address.floor"
        ]);
    }

    // garage or land
    if (propertyType == "GARAGE" || propertyType == "LAND") {
        criteriaToKill.push.apply(criteriaToKill, [
            "property.location.address.propertyName"
        ]);

        if (store.getters["FormModule/getValue"]("property.energy") != null) {
            criteriaToKill.push.apply(criteriaToKill, [
                "property.energy.heatingType",
                "property.energy.hasHeatPump",
                "property.energy.hasPhotovoltaicPanels",
                "property.energy.hasThermicPanels",
                "property.energy.hasCollectiveWaterHeater",
                "property.energy.hasDoubleGlazing",
            ]);
        }
        criteriaToKill.push.apply(criteriaToKill, [
            "property.kitchen.surface",
            "property.kitchen.type",
            "property.laundryRoom.exists",
            "property.livingDescription.netHabitableSurface",
        ]);

        if (store.getters["FormModule/getValue"]("property.privateEquipment") != null) {
            criteriaToKill.push.apply(criteriaToKill, [
                "property.privateEquipment.hasAirConditioning",
                "property.privateEquipment.hasArmoredDoor",
                "property.privateEquipment.hasCableTV",
                "property.privateEquipment.hasDoorPhone",
                "property.privateEquipment.hasInternet",
                "property.commonEquipment.hasSecurityAlarm",
                "property.privateEquipment.hasVisiophone",
            ]);
        }
        criteriaToKill.push.apply(criteriaToKill, [
            "property.showerRoom.count",
            "property.toilet.count",
        ]);

        if (store.getters["FormModule/getValue"]("property.specificities.SME") != null) {
            criteriaToKill.push.apply(criteriaToKill, [
                "property.specificities.SME.office.exists",
                "property.specificities.SME.office.surface",
                "property.specificities.SME.workSpace.exists",
                "property.specificities.SME.workSpace.surface",
            ]);
        }
    }

    // garage
    if (propertyType == "GARAGE") {
        criteriaToKill.push.apply(criteriaToKill, [
            "property.location.surroundings.hasSeaView",
            "property.location.surroundings.type",
            "property.building.facadeCount",
            "property.attic",
            "property.bedroom",
            "property.basement"
        ]);
    }

    // land
    if (propertyType == "LAND") {
        if (store.getters["FormModule/getValue"]("property.commonEquipment") != null) {
            criteriaToKill.push.apply(criteriaToKill, [
                "property.commonEquipment.hasCaretakerOrConcierge",
                "property.commonEquipment.hasDisabledAccess",
                "property.commonEquipment.hasLift",
            ]);
        }
        criteriaToKill.push.apply(criteriaToKill, [
            "property.newlyBuilt.isFirstOccupation",
            "transaction.rental.residential.isFurnished",
            "property.attic",
            "property.bedroom",
            "property.certificates",
            "property.basement",
            "property.building.facadeCount"
        ]);
    }

    if (["HOUSE", "APARTMENT", "OTHER"].indexOf(propertyType) == -1) {
        criteriaToKill.push.apply(criteriaToKill, [
            "property.certificates.globalThermalInsulationLevel",
            "property.certificates.primaryEnergyConsumptionLevel",
            "property.diningRoom.exists",
            "property.diningRoom.surface",
            "property.livingDescription.roomCount"
        ]);
        if (store.getters["FormModule/getValue"]("property.livingDescription") != null) {
            criteriaToKill.push.apply(criteriaToKill, [
                "property.livingDescription.habitableUnitCount",
                "property.livingDescription.roomCount",
                "property.livingDescription.fireplace.count",
            ]);
        }
        criteriaToKill.push.apply(criteriaToKill, [
            "property.livingRoom.exists",
            "property.livingRoom.surface",
        ]);
        if (store.getters["FormModule/getValue"]("property.wellnessEquipment") != null) {
            criteriaToKill.push.apply(criteriaToKill, [
                "property.wellnessEquipment.hasJacuzzi",
                "property.wellnessEquipment.hasSauna",
                "property.wellnessEquipment.hasSwimmingPool",
            ]);
        }
    }

    if ((propertyType == "GARAGE" || propertyType == "APARTMENT" || propertyType == "HOUSE")
      && store.getters["FormModule/getValue"]("property.land") != null
    ) {
        criteriaToKill.push.apply(criteriaToKill, [
            "property.land.hasGasWaterElectricityConnection",
            "property.land.sewerConnection",
            "property.land.surface",
            "property.land.depth",
            "property.land.landWidth",
            "property.land.isFacingStreet",
            "property.land.isFlat",
            "property.land.hasPlotToRear",
            "property.land.isWooded",
            "property.land.totalBuildableGroundFloorSurface",
        ]);
    }

    if (["HOUSE", "OFFICE", "COMMERCIAL", "APARTMENT", "OTHER"].indexOf(propertyType) == -1
      && store.getters["FormModule/getValue"]("property.outdoor") != null
    ) {
        criteriaToKill.push.apply(criteriaToKill, [
            "property.outdoor.garden.exists",
            "property.outdoor.garden.surface",
            "property.outdoor.garden.orientation",
            "property.outdoor.terrace.exists",
            "property.outdoor.terrace.surface",
            "property.outdoor.terrace.orientation",
        ]);
    }

    if (["OFFICE", "INDUSTRY", "COMMERCIAL"].indexOf(propertyType) == -1) {
        if (store.getters["FormModule/getValue"]("property.specificities") != null) {
            criteriaToKill.push.apply(criteriaToKill, [
                "property.specificities.bri.internalFixturesAndFittings.fr",
                "property.specificities.bri.internalFixturesAndFittings.nl",
                "property.specificities.bri.internalFixturesAndFittings.en",
                "property.specificities.bri.miscellaneousFixturesAndFittings.fr",
                "property.specificities.bri.miscellaneousFixturesAndFittings.nl",
                "property.specificities.bri.miscellaneousFixturesAndFittings.en",
                "property.specificities.largeEnterprise.showroomSurface",
                "property.specificities.largeEnterprise.hasReceptionDesk",
                "property.specificities.largeEnterprise.availableFloorSurface",
            ]);
        }
        criteriaToKill.push.apply(criteriaToKill, [
            "transaction.rental.nonResidential.yearlyRentalPrice",
            "transaction.rental.nonResidential.yearlyRentalPricePerSqm",
        ]);
    }

    if(["HOUSE", "APARTMENT", "OTHER", "HOMES_TO_BUILD"].indexOf(propertyType) == -1) {
        if (store.getters["FormModule/getValue"]("property.bedroom") != null) {
            criteriaToKill.push.apply(criteriaToKill, [
                "property.bedroom"
            ]);
        }
    }

    if (propertyType != "HOUSE" && propertyType != "LAND" ) {
        criteriaToKill.push.apply(criteriaToKill, [
            "property.land.latestUseDesignation",
        ]);
    }

    if (propertyType != "INDUSTRY"
      && store.getters["FormModule/getValue"]("property.specificities.industrial") != null
    ) {
        criteriaToKill.push.apply(criteriaToKill, [
            "property.specificities.industrial.accessDoorCount",
            "property.specificities.industrial.sectionalDoorCount",
            "property.specificities.industrial.slidingDoorCount",
            "property.specificities.industrial.loadingBayCount",
            "property.specificities.industrial.coveredBaysCount",
            "property.specificities.industrial.loadingBayWithLiftingDeviceCount",
            "property.specificities.industrial.minAvailableHeight",
            "property.specificities.industrial.maxAvailableHeight",
        ]);
    }

    if (propertyType != "COMMERCIAL"
      && store.getters["FormModule/getValue"]("property.specificities.shop") != null
    ) {
        criteriaToKill.push.apply(criteriaToKill, [
            "property.specificities.shop.shopWindowWidth",
            "property.specificities.shop.hasGoodwill",
            "property.specificities.shop.goodwillPrice",
        ]);
    }

    if (["HOUSE","OFFICE","INDUSTRY","COMMERCIAL","APARTMENT","GARAGE","OTHER","HOMES_TO_BUILD"].indexOf(propertyType) == -1) {
        criteriaToKill.push.apply(criteriaToKill, [
            "property.commonEquipment.hasCaretakerOrConcierge"
        ]);
    }

    // investment property
    if (["APARTMENT_BLOCK","MIXED_USE_BUILDING","MIXED_USE_BUILDING_OFFICES","MIXED_USE_BUILDING_INDUSTRIES","MIXED_USE_BUILDING_COMMERCIAL"].indexOf(propertySubtype as string) == -1) {
        criteriaToKill.push.apply(criteriaToKill, [
            "transaction.investor.currentMonthlyRentalIncome",
            "transaction.investor.occupancyRate"
        ]);
    }

    // isInvestmentProperty
    if (["BUNGALOW","CHALET",
        "APARTMENT","GROUND_FLOOR","DUPLEX","TRIPLEX","FLAT_STUDIO","PENTHOUSE","LOFT","KOT","SERVICE_FLAT",
        "BUILDING_LAND","RECREATIONAL_AREA","FOREST","FIELDS","MEADOW","ORCHARD","UNBUILDABLE_LAND","AGRICULTURAL_AREA","LAND",
        "OUTDOOR_PARKING_SPACE","COVERED_PARKING_SPACE","PROVINCIAL_FARMHOUSE","MILL", "STATIC_CARAVAN","MOBILE_HOME"].indexOf(propertySubtype as string) !== -1) {
        criteriaToKill.push.apply(criteriaToKill, [
            "transaction.investor.isInvestmentProperty"
        ]);
    }

    if (transactionType == "FOR_RENT"
      && store.getters["FormModule/getValue"]("transaction.sale") != null
    ) {
        criteriaToKill.push.apply(criteriaToKill, [
            "transaction.sale",
        ]);
    }

    if (transactionType == "FOR_SALE") {
        criteriaToKill.push.apply(criteriaToKill, [
            "transaction.rental"
        ]);
    }

    if (transactionSubtype != "LIFE_ANNUITY") {
        criteriaToKill.push.apply(criteriaToKill, [
            "transaction.sale.lifeAnnuity"
        ]);
    }

    if (transactionSubtype != "PUBLIC_SALE") {
        criteriaToKill.push.apply(criteriaToKill, [
            "transaction.sale.publicSale"
        ]);
    }

    criteriaToKill.forEach(path => {
        store.commit("FormModule/deleteValue", path);
    });
}

export function addDefaultValuesToStore(
    propertyType: string,
    propertySubtype: string|null,
    transactionType: string,
    transactionSubtype: string|null,
) : void {

    // no conditions
    const defaultValuesToAdd: Array<string|Record<any, any>> = [
        {"path": "property.location.address.isPublic", "value": true},
        "transaction.soldOrRented.isSoldOrRented",
    ];

    if (transactionType == "FOR_RENT") {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "transaction.rental.residential.areSmallPetsAllowed",
            "transaction.rental.residential.areBigPetsAllowed"
        ]);
    }

    if (transactionType == "FOR_SALE") {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "transaction.sale.hasStartingPrice",
            "transaction.sale.isSubjectToVat",
        ]);

        if (transactionSubtype == "LIFE_ANNUITY") {
            defaultValuesToAdd.push.apply(defaultValuesToAdd, [
                "transaction.sale.lifeAnnuity.isIndexed",
                "transaction.sale.lifeAnnuity.isJointAndSurvivorContract",
                "transaction.sale.lifeAnnuity.isBareOwnership",
            ]);
        }

        if (transactionSubtype == "PUBLIC_SALE") {
            defaultValuesToAdd.push.apply(defaultValuesToAdd, [
                "transaction.sale.publicSale.hasUniqueSession",
                "transaction.sale.publicSale.hasHigherBidRight",
                "transaction.sale.publicSale.isForcedSale",
            ]);
        }
    }

    if (propertyType == "COMMERCIAL") {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "property.specificities.shop.hasGoodwill",
        ]);
    }

    if (propertyType != "GARAGE") {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "property.location.surroundings.hasSeaView",
        ]);
    }

    if (propertyType != "LAND") {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "property.commonEquipment.hasCaretakerOrConcierge",
            "property.commonEquipment.hasDisabledAccess",
            "property.commonEquipment.hasLift",
            "property.newlyBuilt.isFirstOccupation",
            // "transaction.rental.residential.isFurnished",
        ]);
    }

    if (propertyType != "GARAGE" && propertyType != "LAND") {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "property.energy.hasHeatPump",
            "property.energy.hasPhotovoltaicPanels",
            "property.energy.hasThermicPanels",
            "property.energy.hasCollectiveWaterHeater",
            "property.energy.hasDoubleGlazing",
            "property.laundryRoom.exists",
            "property.privateEquipment.hasAirConditioning",
            "property.privateEquipment.hasArmoredDoor",
            "property.privateEquipment.hasCableTV",
            "property.privateEquipment.hasDoorPhone",
            "property.privateEquipment.hasInternet",
            "property.commonEquipment.hasSecurityAlarm",
            "property.privateEquipment.hasVisiophone",
            "property.specificities.SME.office.exists",
            "property.specificities.SME.workSpace.exists",
            "property.attic.exists",
            "property.basement.exists"
        ]);
    }

    if(["HOUSE", "APARTMENT", "OTHER", "HOMES_TO_BUILD"].indexOf(propertyType) !== -1) {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "property.bedroom.hasDressingRoom"
        ]);
    }

    if ((propertyType != "GARAGE" && propertyType != "APARTMENT")) {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "property.land.hasGasWaterElectricityConnection",
            "property.land.isFacingStreet",
            "property.land.isFlat",
            "property.land.hasPlotToRear",
            "property.land.isWooded",
        ]);
    }

    if (["HOUSE", "APARTMENT", "OTHER"].indexOf(propertyType) != -1) {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "property.diningRoom.exists",
            "property.livingRoom.exists",
            "property.wellnessEquipment.hasJacuzzi",
            "property.wellnessEquipment.hasSauna",
            "property.wellnessEquipment.hasSwimmingPool",
        ]);
    }

    if (["OFFICE", "INDUSTRY", "COMMERCIAL"].indexOf(propertyType) != -1) {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "property.specificities.largeEnterprise.hasReceptionDesk",
        ]);
    }

    if (["HOUSE", "OFFICE", "COMMERCIAL", "APARTMENT", "OTHER"].indexOf(propertyType) != -1) {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            "property.outdoor.garden.exists",
            "property.outdoor.terrace.exists",
        ]);
    }

    // investment property
    if (["APARTMENT_BLOCK","MIXED_USE_BUILDING","MIXED_USE_BUILDING_OFFICES","MIXED_USE_BUILDING_INDUSTRIES","MIXED_USE_BUILDING_COMMERCIAL"].indexOf(propertySubtype as string) != -1) {
        defaultValuesToAdd.push.apply(defaultValuesToAdd, [
            {"path": "transaction.investor.isInvestmentProperty", "value" : true}
        ]);
    }

    defaultValuesToAdd.forEach((objectOrPath: string|Record<any, any>) => {
        if (typeof objectOrPath === "object") {
            if (store.getters["FormModule/getValue"](objectOrPath.path) === null) {
                store.dispatch("FormModule/addValue", {"path": objectOrPath.path, "value": objectOrPath.value});
            }
        } else {
            if (store.getters["FormModule/getValue"](objectOrPath) === null)
            {
                store.dispatch("FormModule/addValue", {"path": objectOrPath, "value": false});
            }
        }
    });
}

// list all properties per step (use step name in router)
const fieldsPerStep = {
    "step-property-type" : [
        "transaction.type",
        "transaction.availabilityPeriodType",
        "transaction.availabilityDate",
        "property",
        "property.type",
        "property.subtype"
    ],
    "step-property-address": [
        "property.location.address.country",
        "property.location.address.number",
        "property.location.address.postalCode",
        "property.location.address.street",
        "property.location.address.floor",
    ],
    "step-pictures": [],
    "step-property-price": [
        "transaction.sale.price",
        "transaction.sale.pricePerSqm",
        "transaction.sale.cadastralIncome",
        "transaction.rental.monthlyRentalPrice",
        "transaction.rental.nonResidential.yearlyRentalPrice",
        "transaction.rental.nonResidential.yearlyRentalPricePerSqm",
        "property.monthlyCost",
        "property.specificities.shop.goodwillPrice",
        "transaction.investor.isInvestmentProperty",
        "transaction.investor.currentMonthlyRentalIncome",
        "transaction.investor.occupancyRate"
    ],
    "step-general": [
        "property.building.facadeCount",
        "property.building.constructionYear",
        "property.livingDescription.netHabitableSurface",
        "property.land.hasGasWaterElectricityConnection",
        "property.land.sewerConnection",
    ],
    "step-interior": [
        "property.attic.isAccessibleWithFixedStairs",
        "property.attic.surface",
        "property.attic.exists",
        "property.attic.isIsolated",
        "property.specificities.industrial.accessDoorCount",
        "property.specificities.industrial.coveredBaysCount",
        "property.specificities.industrial.loadingBayWithLiftingDeviceCount",
        "property.specificities.industrial.loadingBaysCount",
        "property.specificities.industrial.sectionalDoorCount",
        "property.specificities.industrial.slidingDoorCount",
        "property.specificities.industrial.maxAvailableHeight",
        "property.specificities.industrial.minAvailableHeight",
        "property.specificities.shop.shopWindowWidth",
        "property.toilet.count",
        "property.showerRoom.count",
        "property.bathroom.count",
        "property.bedroom.count",
        "property.bedroom.items.0.surface",
        "property.bedroom.items.1.surface",
        "property.bedroom.items.2.surface",
        "property.bedroom.items.3.surface",
        "property.bedroom.items.4.surface",
        "property.livingDescription.fireplace.count",
        "property.basement.surface",
        "property.specificities.largeEnterprise.availableFloorSurface",
        "property.kitchen.surface",
        "property.specificities.largeEnterprise.showroomSurface",
        "property.privateEquipment.hasArmoredDoor",
        "property.privateEquipment.hasCableTV",
        "property.commonEquipment.hasSecurityAlarm",
        "property.basement.exists",
        "property.basement.surface",
        "property.diningRoom.exists",
        "property.diningRoom.surface",
        "property.bedroom.hasDressingRoom",
        "property.privateEquipment.hasDoorPhone",
        "property.wellnessEquipment.hasJacuzzi",
        "property.wellnessEquipment.hasSauna",
        "property.laundryRoom.exists",
        "property.livingRoom.exists",
        "property.livingRoom.surface",
        "property.livingDescription.roomCount",
        "property.specificities.largeEnterprise.hasReceptionDesk",
        "property.specificities.BRI.internalFixturesAndFittings.en",
        "property.specificities.BRI.internalFixturesAndFittings.fr",
        "property.specificities.BRI.internalFixturesAndFittings.nl",
        "property.specificities.BRI.miscellaneousFixturesAndFittings.en",
        "property.specificities.BRI.miscellaneousFixturesAndFittings.fr",
        "property.specificities.BRI.miscellaneousFixturesAndFittings.nl",
        "property.kitchen.type",

    ],
    "step-exterior": [
        "property.land.surface",
        "property.land.depth",
        "property.land.landWidth",
        "property.land.isFacingStreet",
        "property.land.hasPlotToRear",
        "property.land.isFlat",
        "property.outdoor.garden.exists",
        "property.outdoor.garden.orientation",
        "property.outdoor.garden.surface",
        "property.location.surroundings.hasSeaView",
        "property.wellnessEquipment.hasSwimmingPool",
        "property.outdoor.terrace.exists",
        "property.outdoor.terrace.surface",
        "property.outdoor.terrace.orientation",
        "property.location.surroundings.type",
    ],
    "step-energy": [
        "property.certificates.primaryEnergyConsumptionLevel",
        "property.certificates.globalThermalInsulationLevel",
        "property.certificates.hasElectricalInstallationComplianceCertificate",
        "property.certificates.oilTankCertificateStatus",
        "property.privateEquipment.hasAirConditioning",
        "property.energy.hasCollectiveWaterHeater",
        "property.energy.hasDoubleGlazing",
        "property.energy.hasHeatPump",
        "property.energy.hasPhotovoltaicPanels",
        "property.energy.hasThermicPanels",
        "property.energy.heatingType",
        "transaction.certificates.primaryEnergyConsumption.perSqm",
        "transaction.certificates.epc.description.en",
        "transaction.certificates.epc.description.fr",
        "transaction.certificates.epc.description.nl",
        "transaction.certificates.epc.description",
        "transaction.certificates.carbonEmission",
        "transaction.certificates.epc.reportReference",
        "transaction.certificates.primaryEnergyConsumption.yearlyTheoreticalTotal",
    ],
    "step-regulations": [
        "property.certificates.isAsBuiltPlan",
        "property.constructionPermit.isBreachingUrbanPlanningRegulation",
        "property.constructionPermit.floodZoneType",
        "property.land.latestUseDesignation",
        "property.constructionPermit.isObtained",
        "property.constructionPermit.hasPossiblePriorityPurchaseRight",
        "property.constructionPermit.hasPlotDivisionAuthorization",
        "property.certificates.enforcementMeasuresRegisterConsulted",
    ],
    "step-title-description": [
        "property.description",
        "property.title",
        "property.title.en",
        "property.description.en",
        "property.title.nl",
        "property.description.nl",
        "property.title.fr",
        "property.description.fr"
    ],
    "step-contact-information": ["customerId"],
    "step-pricing": [],
    "step-payment": [],
} as Record<string, Array<string>>;

export function hasWarningsInCurrentStep(apiError: ApiErrorWrapper, currentStep: string): boolean {
    let hasWarning = false;

    const cleanUpWarnings: Array<ApiError> = [];

    store.dispatch("StepsWizard/resetErrors");
    // check if there is any warning in this step
    if(apiError.warnings) {
        apiError.warnings.forEach((warning: ApiError) => {
            forEach(fieldsPerStep, (apiProperties: Array<string>, keyAsStepName: string) => {
                if(apiProperties.includes(warning.property)){
                    store.dispatch("StepsWizard/setErrors", keyAsStepName);
                    cleanUpWarnings.push(warning);
                    if(keyAsStepName === currentStep) {
                        hasWarning = true;
                    }
                }
            });
        });
    }
    // check if there is any error in this step
    if(apiError.errors) {
        apiError.errors.forEach((error: ApiError) => {
            forEach(fieldsPerStep, (apiProperties: Array<string>, keyAsStepName: string) => {
                if(apiProperties.includes(error.property)){
                    store.dispatch("StepsWizard/setErrors", keyAsStepName);
                    cleanUpWarnings.push(error);
                    if(keyAsStepName === currentStep) {
                        hasWarning = true;
                    }
                }
            });
        });
    }

    apiError.warnings = cleanUpWarnings;

    if(hasWarning) {
        store.dispatch("FormModule/setErrors", apiError);
        store.commit("OrdersModule/resetState");
        store.dispatch("StepsWizard/setGroupsUnreachable");
    } else {
        if(cleanUpWarnings.length === 0) {
            store.dispatch("FormModule/setErrors", null);
        } else {
            store.dispatch("FormModule/setErrors", apiError);
        }
    }

    return hasWarning;
}

function getRestrictedPathsFromWarnings(apiData: ApiErrorWrapper | null | undefined): string[] {
    const restrictionMessages = [...(apiData?.warnings || []), ...(apiData?.errors || [])];
    return restrictionMessages
        .filter((w: ApiError) => w.property && (w.type?.startsWith?.("RESTRICTED_") ?? false))
        .map((w: ApiError) => w.property);
}

const OUTDOOR_FEATURES_REQUIRING_SURFACE = ["garden", "terrace"];

// Realo address completion (BasicInfo.vue) can populate outdoor.<feature>.exists = true
// without a surface, which Classified Management always rejects. BasicInfo.vue's own
// retry-cleanup only strips this once, right after it appears; ClassifiedsApi.ts's
// getClassifiedId overwrites the whole `property` object from the backend on every later
// step navigation and can reintroduce it. Since it's a whole-classified PUBLISHABLE check,
// not scoped to the step the warning nominally belongs to, this must be re-checked right
// before order creation rather than relying on the step-scoped warning check to catch it.
export function stripIncompleteOutdoorFeatures(): string[] {
    return OUTDOOR_FEATURES_REQUIRING_SURFACE
        .map((feature) => `property.outdoor.${feature}`)
        .filter((featurePath) => {
            const exists = store.getters["FormModule/getValue"](`${featurePath}.exists`);
            const surface = store.getters["FormModule/getValue"](`${featurePath}.surface`);
            return exists === true && surface == null;
        })
        .map((featurePath) => {
            store.commit("FormModule/deleteValue", featurePath);
            return featurePath;
        });
}

//Checks if a property is located in Flanders, Belgium, based on country code and postal code ranges
export function isPropertyInFlanders(country: string, postalCodeString: string): boolean {
    if (!country) return false;
    const postalCode: number = parseInt(postalCodeString);
    if (isNaN(postalCode)) return false;

    return country.toUpperCase() == "BE" &&
      ((postalCode >= 1500 && postalCode < 4000) ||
        (postalCode >= 8000 && postalCode < 10000));
}

function stripNonPremiumVideo(classified: Record<string, any>): Record<string, any> {
    if (!classified?.media?.externalVideo) {
        return classified;
    }

    const fromTransaction = store.getters["FormModule/getValueTransaction"]("publication.visualizationOption");
    const publication = store.getters["FormModule/getPublication"];
    const selectedPack = (fromTransaction || (publication && publication.visualizationOption) || "").toLowerCase();

    if (selectedPack === "premium") {
        return classified;
    }

    const payload = JSON.parse(JSON.stringify(classified));
    delete payload.media.externalVideo;
    if (Object.keys(payload.media).length === 0) {
        delete payload.media;
    }

    return payload;
}
//Checks if a property is located in the Brussels-Capital Region (postal codes 1000-1299)
export function isPropertyInBrussels(country: string, postalCodeString: string): boolean {
    if (!country) return false;
    const postalCode: number = parseInt(postalCodeString);
    if (isNaN(postalCode)) return false;

    return country.toUpperCase() == "BE" &&
      (postalCode >= 1000 && postalCode < 1300);
}

function executeClassifiedRequest(config: ClassifiedRequestConfig): void {
    const currentStep = store.getters["StepsWizard/getCurrent"];
    const baseUrl: string = process.env.VUE_APP_CLASSIFIEDS_MANAGEMENT_URL || "";
    const version = "1";

    const currentCustomer = store.getters["CustomersModule/getCurrentCustomer"];
    const headers: Record<string, unknown> = {
        "x-api-key": process.env.VUE_APP_CLASSIFIEDS_MANAGEMENT_API_KEY,
        "Accept": "application/json",
        "Authorization": "Bearer " + store.getters["UserModule/getUser"].jwtToken,
    };
    if (currentCustomer) {
        headers["customerId"] = currentCustomer.id;
    }

    const doRequest = (isRetry = false): void => {
        // When editing any existing classified (any status) for a Flemish FOR_SALE, always send
        // enforcementMeasuresRegisterConsulted (default "not determined" = null), even if the
        // regulations step was never visited so the field could initialize itself. The backend
        // restricts this field to FOR_SALE + Flanders, so it is never sent otherwise.
        // Only on the first attempt: on a retry the backend has flagged some paths as restricted
        // and they were deleted, so re-injecting here would undo that and send a stale null.
        const criteriaForEnforcement = store.getters["FormModule/getCriteria"];
        if (!isRetry
            && store.getters["FormModule/getClassifiedId"] !== null
            && ACCEPTABLE_PROPERTY_TYPES.includes(criteriaForEnforcement?.property?.type)
            && criteriaForEnforcement?.transaction?.type === "FOR_SALE"
            && isPropertyInFlanders(
                criteriaForEnforcement?.property?.location?.address?.country,
                criteriaForEnforcement?.property?.location?.address?.postalCode
            )) {
            const certificates = criteriaForEnforcement?.property?.certificates;
            const fieldIsSet = certificates
                && Object.prototype.hasOwnProperty.call(certificates, "enforcementMeasuresRegisterConsulted");
            if (!fieldIsSet) {
                store.commit("FormModule/addValue", {
                    path: "property.certificates.enforcementMeasuresRegisterConsulted",
                    value: null
                });
            }
        }

        if (!ACCEPTABLE_PROPERTY_TYPES.includes(store.getters["FormModule/getValue"]("property.type"))) {
            store.commit("FormModule/deleteValue", "property.certificates");
        }

        let classified = store.getters["FormModule/getCriteria"];
        classified = removeEmptyObjects(classified);
        store.commit("FormModule/setCriteria", classified);

        axios({
            method: config.method,
            url: baseUrl + version + config.uri,
            data: stripNonPremiumVideo(classified),
            headers: headers
        }).then(res => {
            // commit classified id
            if (res.data.id && store.getters["FormModule/getClassifiedId"] === null) {
                store.commit("FormModule/setClassifiedId", res.data.id);
            }

            const restrictedPaths = getRestrictedPathsFromWarnings(res.data);
            if (!isRetry && restrictedPaths.length > 0) {
                restrictedPaths.forEach(path => store.commit("FormModule/deleteValue", path));
                doRequest(true);
                return;
            }

            // if there are warnings for the current step, show errors and stay on the current step
            if (hasWarningsInCurrentStep(res.data, currentStep.step)) {
                config.callbackFunction(false);
            } else {
                config.callbackFunction(true);
            }
        }).catch(e => {
            console.error(e.response);
            const apiData = e.response?.data;
            const restrictedPaths = getRestrictedPathsFromWarnings(apiData);

            if (!isRetry && restrictedPaths.length > 0) {
                restrictedPaths.forEach(path => store.commit("FormModule/deleteValue", path));
                doRequest(true);
                return;
            }

            hasWarningsInCurrentStep(apiData || {}, currentStep.step);
            config.callbackFunction(false);
        });
    };

    doRequest();
}

export function updateClassified(callbackFunction: (success: boolean) => void): void {
    const classifiedId: number|null = store.getters["FormModule/getClassifiedId"];
    executeClassifiedRequest({
        method: classifiedId ? "put" : "post",
        uri: classifiedId ? "/classifieds/" + classifiedId.toString() : "/classifieds",
        callbackFunction: callbackFunction,
    });
}

export function validationClassified(callbackFunction: (success: boolean) => void): void {
    executeClassifiedRequest({
        method: "post",
        uri: "/classifieds/validations",
        callbackFunction: callbackFunction,
    });
}
